import { Component } from 'preact';
import {
	apply,
	debounce,
	ensure,
	formatPrice,
	generateId,
	isIn,
	lengthEq,
	maybe,
	num,
	within,
} from '../../../common/util.js';
import { DesignManager } from '../../lib/design.js';
import { showAlert } from '../popup.js';
import { CartItem, DesignPriceBreakdown } from '../../../common/data/cart.js';
import { Maybe, RosterEntry } from '../../../common/data.js';
import { Icon } from '../icons.js';
import { SizeOnlyRosterEditor } from '../roster.js';
import { Input } from '../input.js';

export interface CartBlockProps {
	roster: Maybe<RosterEntry[]>;
	priceBreakdown?: Maybe<DesignPriceBreakdown>;
	addButtonHandler: () => void;
	rosterButtonHandler: () => void;
	manager: DesignManager;
	cartItem?: CartItem;
}

export interface CartBlockState {
	settingQuantity?: Maybe<number>;
}

export class CartBlock extends Component<CartBlockProps, CartBlockState> {
	
	showInfo = async () => {
		await showAlert('Price does not include graphic design vectorization costs', 'info');
	};
	
	render({ roster, priceBreakdown, addButtonHandler, rosterButtonHandler, cartItem, manager }: CartBlockProps) {
	
		const quantity = this.state.settingQuantity ?? priceBreakdown?.quantity ?? 1;
		const features = ensure(manager.product.features);
		const qtyMode = !features.roster ? 'direct' : lengthEq(features.sizes, 1 ) ? 'osfa' : manager.state.isRosterFixedSized ? 'sizes' : 'roster';
		const rosterOk = quantity > 1 || roster?.[0]?.size;
		
		return <div class="builder-cart-block">
			{priceBreakdown?.errors.map(e => <div class={`builder-cart-block__warning`}>{e.message}</div>)}
			{maybe(priceBreakdown, bd => <>
				{isIn(qtyMode, ['osfa', 'direct']) && <>
					<div class="builder-cart-block__row">
						<span>Quantity:</span>
						<Input
							class="builder-cart-block__quantity-input"
							value={quantity}
							handler={this.setQuantity}
						/>
					</div>
				</>}
				{qtyMode == 'sizes' && <>
					<div class="builder-cart-block__row">
						<span>Quantity:</span>
						<span><strong>{quantity}</strong> total:</span>
					</div>
					<SizeOnlyRosterEditor
						roster={roster ?? []}
						fields={{}}
						product={manager.product}
						replace={manager.replaceRoster}
						onChange={manager.updateRosterEntry}
						onDelete={manager.deleteRosterEntry}
						showPart={null}
					/>
				</>}
				{qtyMode == 'roster' && <div class="builder-cart-block__row">
					<span>Quantity:</span>
					<strong>{quantity}</strong>
					{
						!manager.state.productTemplate ? ''
						: !rosterOk ? <div class="builder-cart-block__extra">
							<span>Complete <a onClick={rosterButtonHandler}>your roster</a> to see your bulk discount.</span>
						</div>
						: <div class="builder-cart-block__extra"><a onClick={rosterButtonHandler}>edit roster</a></div>
					}
				</div>}
				{quantity > 1 && apply(num(bd.productTotal) / bd.quantity, each => <>
					<div class="builder-cart-block__row">
						<strong>{formatPrice(each)} each</strong>
						<button class="builder-cart-block__info" onClick={this.showInfo}>?</button>
						{num(bd.discountTotal) < 0 && <p>(<span>{(((-num(bd.discountTotal) / (num(bd.apparelTotal) + num(bd.extrasTotal)))) * 100).toFixed(1)}%</span> volume discount)</p>}
					</div>
				</>)}
				<div class="builder-cart-block__row">
					<strong>{formatPrice(bd.total)}</strong>
					<span>total</span>
					{apply(
						num(bd.services.filter(s => s.product == 'vector').reduce((a, s) => a + num(s.total), 0)),
						fee => fee > 0 && <span class="builder-cart-block__extra">incl. {formatPrice(fee)} logo vectorization</span>
					)}
					{apply(
						num(bd.services.filter(s => s.product == 'digitize').reduce((a, s) => a + num(s.total), 0)),
						fee => fee > 0 && <span class="builder-cart-block__extra">incl. {formatPrice(fee)} logo digitization</span>
					)}
				</div>
			</>)}
			<button
				class="builder-cart-block__add-button"
				onClick={addButtonHandler}
			>
				<Icon name="cart" />
				{cartItem ? 'Update' : 'Add to Cart'}
			</button>
		</div>;
	}
	
	shouldComponentUpdate(
		nextProps: Readonly<CartBlockProps>,
	) {
		if (nextProps.priceBreakdown != this.props.priceBreakdown) this.setState({ settingQuantity: null });
		return nextProps != this.props;
	}
	
	setQuantity = debounce((qStr: string) => {
		const { manager, roster } = this.props;
		const size = manager.product.features?.sizes[0] ?? 'osfa';
		let quantity = Number(qStr.trim());
		if (!isFinite(quantity)) return;
		quantity = within(1, quantity, 9999);
		const re = roster?.find(re => re.size == size);
		manager.updateRosterEntry(re?.id ?? generateId(), { size, quantity });
		this.setState({ settingQuantity: quantity });
	});
	
}
